import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Layout } from "@/components/layout/Layout";
import { Shield, Mail, Lock, User, ArrowLeft, Loader2 } from "lucide-react";
import { z } from "zod";

const authSchema = z.object({
  email: z.string().email("כתובת אימייל לא תקינה"),
  password: z.string().min(6, "הסיסמה חייבת להכיל לפחות 6 תווים"),
});

const signupSchema = authSchema.extend({
  fullName: z.string().min(2, "שם מלא חייב להכיל לפחות 2 תווים"),
});

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/library");
      }
    };
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session) {
        navigate("/library");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const validateForm = () => {
    try {
      if (isLogin) {
        authSchema.parse({ email, password });
      } else {
        signupSchema.parse({ email, password, fullName });
      }
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            toast({
              title: "שגיאת התחברות",
              description: "אימייל או סיסמה שגויים. נסה שוב.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "שגיאה",
              description: error.message,
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "ברוכים הבאים!",
            description: "התחברת בהצלחה",
          });
          navigate("/library");
        }
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/library`,
            data: {
              full_name: fullName,
            },
          },
        });

        if (error) {
          if (error.message.includes("User already registered")) {
            toast({
              title: "משתמש קיים",
              description: "כתובת האימייל הזו כבר רשומה במערכת. נסה להתחבר.",
              variant: "destructive",
            });
            setIsLogin(true);
          } else {
            toast({
              title: "שגיאה",
              description: error.message,
              variant: "destructive",
            });
          }
        } else {
          toast({
            title: "נרשמת בהצלחה!",
            description: "ברוכים הבאים לספריית החומרים",
          });
          navigate("/library");
        }
      }
    } catch (error) {
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה לא צפויה. נסה שוב.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <section className="min-h-[80vh] flex items-center justify-center section-padding">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
              {isLogin ? "התחברות" : "הרשמה"}
            </h1>
            <p className="text-muted-foreground">
              {isLogin 
                ? "התחבר לחשבון שלך לגישה לספריית החומרים" 
                : "צור חשבון חדש לגישה לספריית החומרים המקצועית"}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-card rounded-3xl p-8 shadow-card border border-border/50">
            <form onSubmit={handleAuth} className="space-y-5">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium">
                    שם מלא
                  </Label>
                  <div className="relative">
                    <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="הזן את שמך המלא"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pr-10 h-12 rounded-xl"
                      disabled={loading}
                    />
                  </div>
                  {errors.fullName && (
                    <p className="text-sm text-destructive">{errors.fullName}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  אימייל
                </Label>
                <div className="relative">
                  <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pr-10 h-12 rounded-xl"
                    dir="ltr"
                    disabled={loading}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  סיסמה
                </Label>
                <div className="relative">
                  <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="לפחות 6 תווים"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10 h-12 rounded-xl"
                    dir="ltr"
                    disabled={loading}
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password}</p>
                )}
              </div>

              <Button 
                type="submit" 
                variant="cta" 
                className="w-full h-12 rounded-xl text-base"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>מעבד...</span>
                  </>
                ) : (
                  <>
                    <span>{isLogin ? "התחבר" : "הירשם"}</span>
                    <ArrowLeft className="w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            {/* Toggle */}
            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-muted-foreground text-sm">
                {isLogin ? "אין לך חשבון?" : "כבר יש לך חשבון?"}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setErrors({});
                  }}
                  className="text-accent font-semibold hover:underline mr-2"
                  disabled={loading}
                >
                  {isLogin ? "הירשם עכשיו" : "התחבר"}
                </button>
              </p>
            </div>
          </div>

          {/* Info */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            הרשמה מאפשרת גישה לספריית החומרים המקצועית שלנו - 
            טמפלטים, מדריכים וסרטונים.
          </p>
        </div>
      </section>
    </Layout>
  );
}
